import { z } from 'zod'
import { AdminRankUpRewardLineDtoType } from '@/schema'

export const rankUpRewardLineSchema = z.object({
	type: z.nativeEnum(AdminRankUpRewardLineDtoType),
	amount: z
		.number({
			required_error: 'กรุณากรอกจำนวน',
			invalid_type_error: 'กรุณากรอกจำนวน',
		})
		.min(1, 'จำนวนต้องมากกว่า 0'),
	luckyWheelSettingId: z.string().optional().nullable(),
	gameId: z.string().optional().nullable(),
	betAmount: z.number().optional().nullable(),
	presetId: z.string().optional().nullable(),
})

export type RankUpRewardLine = z.infer<typeof rankUpRewardLineSchema>

export const createRankFormSchema = z
	.object({
		name: z.string().min(1, 'กรุณากรอกชื่อแรงค์'),
		description: z.string().optional().nullable(),
		frameUrl: z.string().optional().nullable(),
		rankProtectionDeposit: z
			.number({
				required_error: 'กรุณากรอกยอดฝากขั้นต่ำ',
				invalid_type_error: 'กรุณากรอกยอดฝากขั้นต่ำ',
			})
			.min(0, 'กรุณากรอกยอดฝากขั้นต่ำ'),
		rankProtectionTurnover: z
			.number({
				required_error: 'กรุณากรอกยอดเทิร์นขั้นต่ำ',
				invalid_type_error: 'กรุณากรอกยอดเทิร์นขั้นต่ำ',
			})
			.min(0, 'กรุณากรอกยอดเทิร์นขั้นต่ำ'),
		limitWithdrawCredit: z
			.number({
				required_error: 'กรุณากรอกจำกัดการถอนเครดิต',
				invalid_type_error: 'กรุณากรอกจำกัดการถอนเครดิต',
			})
			.min(0, 'กรุณากรอกจำกัดการถอนเครดิต'),
		limitWithdrawFrequency: z
			.number({
				required_error: 'กรุณากรอกจำกัดจำนวนครั้งการถอน',
				invalid_type_error: 'กรุณากรอกจำกัดจำนวนครั้งการถอน',
			})
			.min(0, 'กรุณากรอกจำนวนครั้งการถอน'),
		commissionCashbackId: z.string().nullable().optional(),
		returnLossId: z.string().nullable().optional(),
		dailyCheckInId: z.string().nullable().optional(),
		maxExperiencePoints: z
			.number()
			.min(0, 'คะแนนประสบการณ์สูงสุดต้องมากกว่าหรือเท่ากับ 0')
			.nullable()
			.optional(),
		allowRepeatRankUpRewards: z.boolean().optional().default(false),
		rankUpRewards: z.array(rankUpRewardLineSchema).optional().default([]),
	})
	.superRefine((data, ctx) => {
		const rewards = data.rankUpRewards ?? []
		const typeCounts = new Map<string, number>()

		for (const reward of rewards) {
			typeCounts.set(reward.type, (typeCounts.get(reward.type) ?? 0) + 1)
		}

		for (const [type, count] of typeCounts) {
			if (count > 1) {
				const duplicateIndices = rewards
					.map((r, i) => (r.type === type ? i : -1))
					.filter((i) => i >= 0)

				for (const idx of duplicateIndices.slice(1)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'ไม่สามารถเพิ่มรางวัลประเภทเดียวกันได้',
						path: ['rankUpRewards', idx, 'type'],
					})
				}
			}
		}

		for (let i = 0; i < rewards.length; i++) {
			const reward = rewards[i]
			if (reward.type === AdminRankUpRewardLineDtoType.FREE_SPIN) {
				if (!reward.gameId) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'กรุณาเลือกเกม',
						path: ['rankUpRewards', i, 'gameId'],
					})
				}
				if (!reward.presetId) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'กรุณาเลือก RTP Preset',
						path: ['rankUpRewards', i, 'presetId'],
					})
				}
				if (!reward.betAmount || reward.betAmount <= 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'กรุณากรอกยอดแทง',
						path: ['rankUpRewards', i, 'betAmount'],
					})
				}
			}
		}
	})
